/**
 * Build ICS (RFC 5545) calendar content from one or more event sessions so
 * users can add the event to their calendar app.
 *
 * Pure string-builder — DOM side effects (Blob, anchor click, download) live
 * in the calling component.
 */

export interface IcsEventDay {
  startDate?: string | null;
  endDate?: string | null;
}

/**
 * Convert an ISO-ish datetime ("YYYY-MM-DDTHH:mm:ss" or full ISO) to the
 * compact ICS format "YYYYMMDDTHHMMSSZ". Returns "" for missing or invalid
 * input so callers can skip empty dates without try/catch.
 */
export function toIcsDateString(isoDate?: string | null): string {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return "";
  return date
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\.\d{3}/, "");
}

/**
 * Build the full VCALENDAR string. Days with no parseable startDate are
 * silently skipped — an event with all-bad days yields a header-only file,
 * which is harmless to import.
 */
export function buildIcsFile(summary: string, days: IcsEventDay[]): string {
  const events = days
    .map((day) => {
      const startIcs = toIcsDateString(day.startDate);
      const endIcs = toIcsDateString(day.endDate);
      if (!startIcs) return null;
      // toIcsDateString already returns the UTC suffix (e.g. "20260415T130000Z").
      // Don't append another "Z" — RFC 5545 wants a single trailing Z and some
      // calendar apps reject "...ZZ" as invalid.
      return [
        "BEGIN:VEVENT",
        `DTSTART:${startIcs}`,
        endIcs ? `DTEND:${endIcs}` : "",
        `SUMMARY:${summary}`,
        "END:VEVENT",
      ]
        .filter(Boolean)
        .join("\r\n");
    })
    .filter(Boolean);

  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    ...events,
    "END:VCALENDAR",
    "",
  ].join("\r\n");
}
